import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { Post } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import ImageUpload from './ImageUpload';

const PostCards: React.FC = () => {
  const { t } = useScript();
  const { isAdmin, token } = useAuth();
  const navigate = useNavigate();

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [postForm, setPostForm] = useState<Partial<Post>>({});
  const [lightboxPost, setLightboxPost] = useState<Post | null>(null);

  // Stable ref for t() — avoids re-fetching data when translations arrive.
  const tRef = useRef(t);
  tRef.current = t;

  const loadPosts = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getPosts()
      .then(items => { setPosts(items); setLoading(false); })
      .catch((e: ApiError) => {
        setError(e?.message || tRef.current('Грешка при учитавању.'));
        setLoading(false);
      });
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const savePost = async () => {
    if (!postForm.id || !token) return;
    try {
      const updated = await api.updatePost(token, postForm.id, postForm);
      setPosts(posts.map(p => p.id === updated.id ? updated : p));
      setEditingPostId(null);
    } catch {}
  };

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
  };

  const startEdit = (post: Post) => {
    setEditingPostId(post.id);
    setPostForm(post);
  };

  // Close lightbox on Escape key
  useEffect(() => {
    if (!lightboxPost) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxPost(null);
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [lightboxPost]);

  if (loading) {
    return (
      <section className="gallery-mosaic-section">
        <div className="state-block" role="status" aria-live="polite">
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="gallery-mosaic-section">
        <div className="state-block state-error" role="alert">
          <span>{error}</span>
          <button type="button" className="state-retry" onClick={loadPosts}>
            {t('Покушај поново')}
          </button>
        </div>
      </section>
    );
  }

  // Take featured posts (or first few) for the mosaic grid
  const allPosts = (() => {
    const featured = posts.filter(p => p.featured).slice(0, 8);
    return featured.length > 0 ? featured : posts.slice(0, 8);
  })();

  // We'll build a grid: posts fill cells, with the center cell being the logo
  // For a 3-column layout: positions 0,1,2 (row1), 3=post, 4=LOGO, 5=post (row2), 6,7,8 (row3)
  // We insert the logo at position 4 (center of 3x3)
  const gridItems: Array<{ type: 'post'; post: Post; index: number } | { type: 'logo' }> = [];
  let postIdx = 0;
  const totalCells = Math.max(9, allPosts.length + 1); // at least 3x3
  const centerIndex = 4; // center of 3x3

  for (let i = 0; i < totalCells && i < 9; i++) {
    if (i === centerIndex) {
      gridItems.push({ type: 'logo' });
    } else if (postIdx < allPosts.length) {
      gridItems.push({ type: 'post', post: allPosts[postIdx], index: postIdx });
      postIdx++;
    }
  }

  return (
    <section className="gallery-mosaic-section">
      <div className="gallery-mosaic-header">
        <div className="gallery-mosaic-ornament-line" />
        <h2 className="gallery-mosaic-title">{t('Галерија')}</h2>
        <div className="gallery-mosaic-ornament-line" />
      </div>

      <div className="gallery-mosaic-grid">
        {gridItems.map((item, idx) => {
          if (item.type === 'logo') {
            return (
              <div key="logo-center" className="gallery-mosaic-cell gallery-mosaic-logo-cell">
                <img
                  src="/resource/logo/1.png"
                  alt="Манастир Градац"
                  className="gallery-mosaic-logo-img"
                />
              </div>
            );
          }

          const { post, index } = item;
          const isEditing = editingPostId === post.id;

          return (
            <div
              key={post.id}
              className="gallery-mosaic-cell gallery-mosaic-image-cell"
              onClick={() => {
                if (!isAdmin && !isEditing) setLightboxPost(post);
              }}
            >
              {/* Background image */}
              <img
                src={isEditing && postForm.imageUrl ? postForm.imageUrl : post.imageUrl}
                alt={post.title}
                className="gallery-mosaic-cell-img"
                onError={handleImgError}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />

              {/* Overlay gradient */}
              <div className="gallery-mosaic-cell-overlay" />

              {/* Content */}
              <div className="gallery-mosaic-cell-content">
                {isAdmin && isEditing ? (
                  <div className="gallery-mosaic-admin-form" onClick={e => e.stopPropagation()}>
                    <ImageUpload
                      label={t("URL слике")}
                      imageUrl={postForm.imageUrl ?? ''}
                      onImageUrlChange={url => setPostForm({ ...postForm, imageUrl: url })}
                    />
                    <div>
                      <label className="gallery-mosaic-admin-label">{t("Наслов")}</label>
                      <input
                        type="text"
                        className="gallery-mosaic-admin-input"
                        value={postForm.title ?? ''}
                        onChange={e => setPostForm({ ...postForm, title: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="gallery-mosaic-admin-label">{t("Опис")}</label>
                      <textarea
                        rows={4}
                        className="gallery-mosaic-admin-textarea"
                        value={postForm.description ?? ''}
                        onChange={e => setPostForm({ ...postForm, description: e.target.value })}
                      />
                    </div>
                    <div className="gallery-mosaic-admin-actions">
                      <button className="btn-primary" onClick={savePost}>{t("Сачувај")}</button>
                      <button className="btn-secondary" onClick={() => setEditingPostId(null)}>{t("Откажи")}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="gallery-mosaic-cell-plus">✦</span>
                    <h3 className="gallery-mosaic-cell-title">{t(post.title)}</h3>
                    {isAdmin && (
                      <button className="gallery-mosaic-edit-btn" onClick={(e) => { e.stopPropagation(); startEdit(post); }}>
                        {t("Уреди")} ✎
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="gallery-mosaic-actions">
        {isAdmin ? (
          <button type="button" className="news-all-btn" onClick={() => navigate('/galerija/nova')}>
            {t('+ Додај')}
          </button>
        ) : (
          <Link to="/galerija" className="gallery-mosaic-view-all">
            <span className="gallery-mosaic-view-all-text">{t('Погледајте галерију')}</span>
            <span className="gallery-mosaic-view-all-arrow">→</span>
          </Link>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxPost && (
        <div className="glightbox-backdrop" onClick={() => setLightboxPost(null)}>
          <div className="glightbox-container" onClick={e => e.stopPropagation()}>
            <button
              className="glightbox-close"
              onClick={() => setLightboxPost(null)}
              aria-label={t('Затвори')}
            >×</button>
            <div className="glightbox-image-wrapper">
              <img
                src={lightboxPost.imageUrl}
                alt={lightboxPost.title}
                className="glightbox-image"
              />
            </div>
            <div className="glightbox-info">
              <h3 className="glightbox-title">{t(lightboxPost.title)}</h3>
              <p className="glightbox-description">{t(lightboxPost.description)}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PostCards;
